/* eslint-disable sort-keys */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AddLocationComponent } from '../../add-location/containers/add-location.component';
import { LocationsFilterOption } from '../models/locations-filter-option';
import { LocationsOrderingOptions, LocationsRegisterOptions } from '../models/locations-register-options';
import { LocationsRegisterListItem, LocationsRegisterResults } from '../models/locations-register-results.model';
import { GetLocations, GetLocations_locationsConnection_items } from '../queries/generated/GetLocations';
import { LocationRegisterSessionStorageService } from '../services/location-register-session.storage.service';
import { LocationsRegisterApiService } from '../services/locations-register-api.service';
import {
   createLocation,
   locationsRegisterAdapterActions,
   setOrderingOptionsFromSessionStorage,
} from './locations-register.actions';
import { selectEndCursor, selectOrderingOptions, selectQuickFilters } from './locations-register.reducer';
import * as LocationsRegisterSelectors from './locations-register.selectors';
import { selectFilters } from './locations-register.selectors';

@Injectable()
export class LocationsRegisterEffects {
   addLocation$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createLocation),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddLocationComponent, {
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadLocationsList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(locationsRegisterAdapterActions.pageEnter),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([, filters, quickFilters, orderingOptions]) => {
            const sessionOrderingOptions = this.locationRegisterSessionStorageService.getLocationOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   loadLocationPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(locationsRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(LocationsRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, endCursor, quickFilters, orderingOptions]) => {
            const sessionOrderingOptions = this.locationRegisterSessionStorageService.getLocationOrderingOptions();
            if (sessionOrderingOptions) {
               orderingOptions = sessionOrderingOptions;
               this.store.dispatch(setOrderingOptionsFromSessionStorage({ orderingOptions }));
            }
            return this.loadNewPage(endCursor, filters, quickFilters, orderingOptions);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(locationsRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, quickFilters, filters, orderingOptions]) => {
            this.locationRegisterSessionStorageService.setLocationOrderingOptions(orderingOptions);
            return this.loadNewData(filters, quickFilters, orderingOptions);
         })
      );
   });

   orderingOptionsChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(locationsRegisterAdapterActions.orderingOptionsChanged),
         withLatestFrom(
            this.store.pipe(select(selectFilters)),
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(selectOrderingOptions))
         ),
         switchMap(([action, filters, quickfilters, orderingOptions]) => {
            this.locationRegisterSessionStorageService.setLocationOrderingOptions(orderingOptions);
            return this.loadNewData(filters, quickfilters, orderingOptions);
         })
      );
   });

   dialogId: string;

   constructor(
      private actions$: Actions,
      private store: Store<AppState>,
      private locationsRegisterApiService: LocationsRegisterApiService,
      private locationRegisterSessionStorageService: LocationRegisterSessionStorageService,
      private dialogService: LcsDialogService
   ) {}

   private loadNewData(
      filters: LocationsFilterOption[],
      quickFilters: LocationsRegisterOptions | null = null,
      orderingOptions: LocationsOrderingOptions[] | null = null
   ) {
      return this.locationsRegisterApiService.getLocations(null, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return locationsRegisterAdapterActions.loadSuccess(
               this.convertGetLocationsResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(locationsRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: LocationsFilterOption[],
      quickFilters: LocationsRegisterOptions | null = null,
      orderingOptions: LocationsOrderingOptions[] | null = null
   ) {
      return this.locationsRegisterApiService.getLocations(endCursor, filters, quickFilters, orderingOptions).pipe(
         map((result) => {
            return locationsRegisterAdapterActions.loadPageSuccess(
               this.convertGetLocationsResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(locationsRegisterAdapterActions.loadPageFailure(error));
         })
      );
   }

   private convertGetLocationsResultToLoadSuccessProps(getLocations: GetLocations): LocationsRegisterResults {
      return {
         endCursor: getLocations.locationsConnection.pageInfo.endCursor,
         hasNextPage: getLocations.locationsConnection.pageInfo.hasNextPage,
         items: getLocations.locationsConnection.items.map(this.convertLocationsRegisterListItem.bind(this)),
         totalCount: getLocations.locationsConnection.totalCount,
      };
   }

   private convertLocationsRegisterListItem(
      getLocationItem: GetLocations_locationsConnection_items
   ): LocationsRegisterListItem {
      return {
         locationId: getLocationItem.locationId,
         address: getLocationItem.address,
         city: getLocationItem.city,
         name: getLocationItem.name,
         state: getLocationItem.state,
         postalCode: getLocationItem.postalCode,
         createDate: getLocationItem.createDate,
         updateDate: getLocationItem.updateDate,
         createUser: getLocationItem.createUser?.name || null,
         updateUser: getLocationItem.updateUser?.name || null,
      };
   }
}
